/**
 * Area Code Generator
 *
 * Generates unique area codes based on building type.
 * Format: {PREFIX}-{NUMBER:03d}
 * Example: COM-001, T-002, RMP-001
 */

import { supabase } from '@/lib/supabase'
import { AREA_TYPE_PREFIXES, type HangarType } from '@/types/hangars'

/**
 * Generate the next available area code for a given building type
 *
 * @param organizationId - The account/organization ID
 * @param type - The building type (community, t_hangar, ramp, etc.)
 * @returns Promise<string> - The next available code (e.g., "COM-001")
 */
export async function generateAreaCode(
  organizationId: string,
  type: HangarType
): Promise<string> {
  const prefix = AREA_TYPE_PREFIXES[type] || 'AREA'

  // Query existing area codes with this prefix for the account
  const { data, error } = await supabase
    .from('areas')
    .select('area_code')
    .eq('organization_id', organizationId)
    .ilike('area_code', `${prefix}-%`)
    .order('area_code', { ascending: false })
    .limit(10)

  if (error) {
    console.error('Error fetching area codes:', error)
    // Fall back to timestamp-based code if query fails
    return `${prefix}-${Date.now().toString().slice(-3)}`
  }

  // Find the highest number from existing codes
  let maxNumber = 0

  if (data && data.length > 0) {
    for (const row of data) {
      const code = row.area_code as string
      // Extract number from code (e.g., "COM-042" -> 42)
      const match = code.match(new RegExp(`^${prefix}-(\\d+)$`))
      if (match) {
        const num = parseInt(match[1], 10)
        if (num > maxNumber) {
          maxNumber = num
        }
      }
    }
  }

  // Generate next code
  const nextNumber = maxNumber + 1
  const paddedNumber = nextNumber.toString().padStart(3, '0')

  return `${prefix}-${paddedNumber}`
}

/**
 * Get the prefix for a building type
 */
export function getAreaTypePrefix(type: HangarType): string {
  return AREA_TYPE_PREFIXES[type] || 'AREA'
}

/**
 * Parse an area code into its components
 *
 * @param code - The area code (e.g., "COM-042")
 * @returns Object with prefix and number, or null if invalid format
 */
export function parseAreaCode(code: string): { prefix: string; number: number } | null {
  const match = code.match(/^([A-Z]+)-(\d+)$/)
  if (!match) return null

  return {
    prefix: match[1],
    number: parseInt(match[2], 10),
  }
}
